import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {WinPresentationController} from "@nolimitcity/slot-game/bin/core/winpresentation/WinPresentationController";
import {TimelineLite} from "gsap";
import {ThorGameMode} from "../../gamemode/ThorGameMode";
import {ThorParsedGameData} from "../../server/data/ThorParsedGameData";
import {ThorParsedInitData} from "../../server/data/ThorParsedInitData";
import {AnimatedScatter} from "../../symbols/AnimatedScatter";
import {ThorGameModuleConfig} from "../../ThorGameModuleConfig";
import {ThorScatterNudgeWPView} from "./ThorScatterNudgeWPView";

/**
 * Created by Jerker Nord on 2018-12-20.
 */

export class ThorScatterNudgeWPController extends WinPresentationController {

    public static readonly PLAY_SCATTER_COLLECT_ANIMATION:string = "ThorScatterNudgeController_playScatterCollect";

    private _scatterNudge:boolean = false;
    private _reelsToNudge:boolean[];
    private _collectedScattersAfter:number;
    private _collectedScatterBefore:number;
    private _scattersNudgedOut:number = 0;
    private _reels:string[][];
    private _scatterPositions:number[];
    private _topSyms:string[];
    private _scattersToPlayCollect:AnimatedScatter[];
    private _thorScatterNudgeWPView:ThorScatterNudgeWPView;
    private _isExtraFS:boolean = false;
    private _scattersCollectTimeline:TimelineLite;
    private _nudgeTL:TimelineLite;
    private _collectedScatters:number;
    private _landedScatterCount:number;
    private _meterSpinsCounterAfter:number;
    private _landedScatterCountAfter:number;
    private _nextMode:string;

    constructor(indexes:[number, number]) {
        super(indexes, {name : "ThorScatterNudgeWPController", skippable : true});
        this._reelsToNudge = [false, false, false, false, false];
        this._scattersToPlayCollect = [];
        this._thorScatterNudgeWPView = new ThorScatterNudgeWPView();
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, (event:GameEvent) => this.onRestore());
        EventHandler.addEventListener(this, ThorScatterNudgeWPController.PLAY_SCATTER_COLLECT_ANIMATION, (event:GameEvent):void => this.onPlayScatterCollectAnimation(event.params[0]));
    }

    private onInitDataParsed(data:ThorParsedInitData):void {
        this._landedScatterCount = data.landedScatterCount;
        this._collectedScatters = data.collectedScatters;
        this._nextMode = data.nextMode;
    }

    private onRestore():void {
        if(this._nextMode == ThorGameMode.SCATTER_RESPIN) {
            if(this._collectedScatters == 0 && this._landedScatterCount > 0) {
                for(let i:number = 0; i < this._landedScatterCount; i++) {
                    ThorGameModuleConfig.thorFreeSpinBoxView.getUpdateFreeSpinBoxMeterTL().restart();
                }
            }
        }

        if(this._collectedScatters > 0) {
            if(this._nextMode == ThorGameMode.SCATTER_RESPIN) {
                for(let i:number = 0; i < this._landedScatterCount; i++) {
                    ThorGameModuleConfig.thorFreeSpinBoxView.getUpdateFreeSpinBoxMeterTL().restart();
                }
            }
            if(this._collectedScatters > 2) {
                ThorGameModuleConfig.thorFreeSpinBoxView.updateFSNumber(this._collectedScatters);
            }
        }
    }

    private onPlayScatterCollectAnimation(animatedScatter:AnimatedScatter):void {
        this._scattersToPlayCollect.push(animatedScatter);
    }

    protected parseFeatureGameData(data:ThorParsedGameData):void {
        this._collectedScatterBefore = data.collectedScattersBefore;
        this._collectedScattersAfter = data.collectedScattersAfter;
        this._meterSpinsCounterAfter = data.meterSpinsCounterAfter;
        this._scattersNudgedOut = this._collectedScattersAfter;
        this._landedScatterCountAfter = data.landedScatterCountAfter;

        this._reels = data.reels;
        if(this._collectedScattersAfter > 2) {
            this._isExtraFS = true;
        } else {
            this._isExtraFS = false;
        }

        if(data.mode == ThorGameMode.SCATTER_RESPIN || data.nextMode == ThorGameMode.SCATTER_RESPIN) {
            this._scatterNudge = true;
            this._topSyms = [];
            this._scatterPositions = <number[]>(data.scatterPositions);
            for(let i:number = 0; i < data.reels.length; i++) {
                this._topSyms.push(data.reels[i][2]);
                for(let j:number = 3; j < 6; j++) {
                    if(data.reels[i][j] == "S") {
                        this._reelsToNudge[i] = true;
                        break;
                    } else {
                        this._reelsToNudge[i] = false;
                    }
                }
            }
        } else {
            this._scatterNudge = false;
        }
    }

    protected hasWin():boolean {
        return this._scatterNudge;
    }

    protected startWinPresentation():void {
        if(this._nudgeTL && this._nudgeTL.isActive()) {
            this._nudgeTL.pause();
            this._nudgeTL.kill();
        }
        this._nudgeTL = new TimelineLite();
        if(this._scattersToPlayCollect.length > 0) {
            this._nudgeTL.add(this.getScattersCollectAnimation());
        }
        this._nudgeTL.add(() => this.finish());
    }

    private getScattersCollectAnimation():TimelineLite {
        if(this._scattersCollectTimeline && this._scattersCollectTimeline.isActive()) {
            this._scattersCollectTimeline.pause();
            this._scattersCollectTimeline.kill();
        }
        this._scattersCollectTimeline = new TimelineLite();
        let scattersCollectTimelines:TimelineLite[] = [];
        for(let i:number = 0; i < this._scattersToPlayCollect.length; i++) {
            const animatedScatter:AnimatedScatter = this._scattersToPlayCollect[i];
            let scatterTimeline:TimelineLite = new TimelineLite();
            scatterTimeline.add(() => animatedScatter.stopMoreScatterTimeline());
            scatterTimeline.add(() => animatedScatter.playScatterCollectTL());
            scatterTimeline.add(this._thorScatterNudgeWPView.getScatterEnergyTL(animatedScatter.reelId, animatedScatter.symbolPosition, this.getIsExtraFs()), "+=0.4");
            scatterTimeline.add(ThorGameModuleConfig.thorFreeSpinBoxView.getUpdateFreeSpinBoxMeterTL(), "+=0.7");
            scattersCollectTimelines.push(scatterTimeline);
        }
        this._scattersCollectTimeline.add(scattersCollectTimelines);

        return this._scattersCollectTimeline;
    }

    private getIsExtraFs():boolean {
        return this._isExtraFS;
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(isAborted) {
            if(this._nudgeTL && this._nudgeTL.isActive()) {
                this._nudgeTL.pause();
                this._nudgeTL.kill();
            }
            if(this._scattersCollectTimeline && this._scattersCollectTimeline.isActive()) {
                this._scattersCollectTimeline.pause();
                this._scattersCollectTimeline.kill();
            }

            if(ThorGameModuleConfig.thorScatterRespinController.firstScatter) {
                ThorGameModuleConfig.thorScatterRespinController.firstScatter.abortScatterToNormalState();
            }

            for(let i:number = 0; i < this._scattersToPlayCollect.length; i++) {
                this._scattersToPlayCollect[i].abortScatterToNormalState();
            }

            let meterLevel:number = 0;
            if(this._collectedScattersAfter == 0 && this._landedScatterCountAfter > 0) {
                for(let i:number = 0; i < this._landedScatterCountAfter; i++) {
                    meterLevel++;
                }
            }
            if(this._collectedScattersAfter > 0) {
                for(let i:number = 0; i < this._landedScatterCountAfter; i++) {
                    meterLevel++;

                }
                if(meterLevel > 3) {
                    let fs:number = 10 + ((this._landedScatterCountAfter - 3) * 2);
                    ThorGameModuleConfig.thorFreeSpinBoxView.updateFSNumber(fs);
                }
            }

            ThorGameModuleConfig.thorFreeSpinBoxView.abortFreeSpinBoxMeter();
        }
        this._scattersToPlayCollect = [];
    }
}